import moment from "moment";

import { hasInterestRateChart } from "../../../../helpers/depositType";
import {
  TCreateInvestmentProductState,
  TInvestmentProductDetails,
} from "../CreateInvestmentProduct";
import { needsWithdrawalWindows, SEND_PRODUCT_TYPE_ID } from "./planType";

/**
 * The fund as the API takes it.
 *
 * Create and Edit built this twice, with the same four rules written out each
 * time and drifting between them. It is one function now, and the one place
 * to look when the server refuses a fund.
 *
 * The four rules, all of which the server enforces:
 *
 * - Dates go as `DD/MM/YYYY`, not the ISO the pickers work in.
 * - An interest rate chart is required for a savings deposit and **rejected**
 *   for every other deposit type, so the key is present or absent rather than
 *   sent as null.
 * - The withdrawal window trio and the lock-in period belong to Habit Save
 *   and are meaningless for anything else.
 * - An empty early liquidation rate means early withdrawal is free, so it is
 *   left out rather than sent as 0 or null.
 * - `termSheetFile` holds a `File`, which serialises to `{}` — it rides the
 *   FormData separately.
 */

const API_DATE_FORMAT = "DD/MM/YYYY";

export type TFundPayload = Record<string, any>;

/**
 * `investmentInterestPaymentType` is the read model's nested object; the DTO
 * takes `investmentInterestPaymentTypeId` and rejects the object beside it.
 * Applied to every currency — it used to be deleted from index 0 only, so a
 * multi-currency fund sent it on the rest.
 */
function stripNestedPaymentType(detail: any): any {
  const { investmentInterestPaymentType, ...rest } = detail ?? {};

  return rest;
}

/** One currency, reduced to the fields the DTO declares. */
function toDetailPayload(detail: TInvestmentProductDetails): TFundPayload {
  return {
    ...(detail?.id ? { id: detail.id } : {}),
    coreBankingProductId: Number(detail?.coreBankingProductId),
    coreBankingTenantId: detail?.coreBankingTenantId,
    currencyCode: detail?.currencyCode,
    maximumInvestment: Number(detail?.maximumInvestment),
    minimumInvestment: Number(detail?.minimumInvestment),
    externalWalletProvider: Number(detail?.externalWalletProvider),
    depositType: Number(detail?.depositType),
    ...(hasValue(detail?.earlyLiquidationChargePercentage)
      ? {
          earlyLiquidationChargePercentage: Number(
            detail.earlyLiquidationChargePercentage,
          ),
        }
      : {}),
    investmentInterestPaymentTypeId:
      (detail as any)?.investment_interest_payment_type?.id ??
      detail?.investmentInterestPaymentTypeId,
    termsAndConditionId: detail?.termsAndConditionId,
    ...(detail?.isLocked === undefined
      ? {}
      : { isLocked: detail.isLocked === true || detail.isLocked === "true" }),
    ...(hasInterestRateChart(detail?.depositType)
      ? { interestRateChart: detail?.interestRateChart }
      : {}),
  };
}

export function buildFundPayload(
  state: TCreateInvestmentProductState,
): TFundPayload {
  const payload: TFundPayload = { ...state };

  payload.investmentProductDetails = (state.investmentProductDetails ?? []).map(
    (detail) => stripNestedPaymentType(toDetailPayload(detail)),
  );

  payload.startDate = moment(state.startDate).format(API_DATE_FORMAT);
  payload.endDate = moment(state.endDate).format(API_DATE_FORMAT);

  // The API owner says he sets productTypeId himself; the handler stores the
  // request value verbatim and nothing infers it, so it goes out from here
  // while that is open. See Q1 in docs/ADMIN_BACKEND_QUESTIONS.md.
  if (!SEND_PRODUCT_TYPE_ID) {
    delete payload.productTypeId;
  }

  if (!needsWithdrawalWindows(state.productTypeId)) {
    delete payload.withdrawalWindowInterval;
    delete payload.withdrawalWindowIntervalUnit;
    delete payload.gracePeriod;
  }

  if (
    !needsWithdrawalWindows(state.productTypeId) ||
    !hasValue(state.lockInPeriod)
  ) {
    delete payload.lockInPeriod;
  }

  delete payload.termSheetFile;

  return payload;
}

/** The fund and its term sheet, ready to post. */
export function toFundFormData(state: TCreateInvestmentProductState): FormData {
  const formData = new FormData();

  formData.append(
    "InvestmentProductDto",
    JSON.stringify(buildFundPayload(state)),
  );

  if (state.termSheetFile?.file) {
    formData.append(
      "TermSheet",
      state.termSheetFile.file,
      state.termSheetFile.fileName,
    );
  }

  return formData;
}

function hasValue(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && !Number.isNaN(Number(value));
}
